import React, { useState, useEffect } from "react";
import cx from "classnames";
import { BlockCopy } from "src/components/block-copy";
import { Image } from "src/components/image";
import { ProductImageCarousel } from "src/components/product/image-carousel";
import { VariantOptions } from "src/components/product/variant-options";
import { ProductTitle } from "src/components/product-title";
import { Logotype } from "src/components/logotype";
import { Link as ScrollLink } from "react-scroll";
import { Price } from "src/components/price";

import CourseHero from './CourseHero'
import MediaPlayer from '../../media/MediaPlayer'
import {Play} from '../../media/Icons'

import {
  useToggleMediaPlayer,
  useSetCourseMedia,
  useSetCurrentTrack,
  useStoreContext,
} from "../../../context/siteContext";

import styles from "./hero.module.css";

export const ProductHero = ({
  main,
  product,
  liveVariants,
  productOptions,
  activeLiveVariant,
  activeVariantId,
  onVariantSelect,
  compareAtPrice,
}: {
  main: {
    title: string;
    slug: {};
    productDetails?: [];
    productSpecs?: [
      {
        _key: string;
        title: string;
        spec: string;
      }
    ];
    mainImage: {
      _key: string;
      asset: {
        _id: string;
      };
      variant?: {};
    };
    productCourse?: {
      isCourse?: boolean;
      _type?: string;
      courseInstructor?: string;
      audioTrailer?: {
        asset?: {
          url?: string;
        };
      };
      videoTrailer?: {
        video?: any;
      };
      courseDescription?: {
        description: [any];
      };
      courseCover?: {
        asset: any;
      };
      courseContent?: [any];
    };
    modules: [
      {
        _key: string;
        enableNavigationLink: boolean;
        navigationLinkTitle: string;
        images: [
          {
            _key?: string | undefined;
            asset: {
              _id: string;
            };
            alt: string;
          }
        ];
      }
    ];
  };
  product: {
    defaultVariant: {
      variantId: number;
      sku: number;
    };
    defaultPrice: string;
    productType: string;
    productId: number;
    title: string;
    variants: [];
  };
  liveVariants: any;
  productOptions: any;
  activeLiveVariant: any;
  activeVariantId: number;
  onVariantSelect: (
    e: React.FormEvent,
    variantId: number,
    variantAvailable: boolean
  ) => void;
  compareAtPrice: any;
}) => {
  const {
    title,
    productDetails,
    productSpecs,
    mainImage,
    modules,
    productCourse,
  } = main;

  const moduleImages =
    modules &&
    modules
      .map((section) => {
        if (section.images?.length > 0) {
          return section.images.map((image: any) => image);
        }
      })
      .flat();

  const productImages = moduleImages
    ? [mainImage, ...moduleImages]
    : [mainImage];

  const isCourse = productCourse && productCourse.isCourse === true;

  const setMediaPlayerOpen = useToggleMediaPlayer();
  const setCourseMedia = useSetCourseMedia();
  const setCurrentTrack = useSetCurrentTrack();

  const { isMediaPlayerOpen } = useStoreContext();


  const isFootwear = product.productType.toLowerCase() === "footwear";

  const playTrailer = () => {
    const audioCourse = !!productCourse.audioTrailer;
    const videoCourse = !!productCourse.videoTrailer;

    if (!productCourse.audioTrailer && !productCourse.videoTrailer) return;

    const trailerTrack = {
      title: `Trailer — ${main.title}`,
      audioUrl: audioCourse ? productCourse.audioTrailer.asset.url : undefined,
      video: videoCourse ? productCourse.videoTrailer.asset : undefined,
    };

    setCurrentTrack(trailerTrack);
    setCourseMedia(productCourse.courseType === "audio" ? "audio" : "video", [
      trailerTrack,
    ]);
    setMediaPlayerOpen(true);
  };

  return (
    <React.Fragment>
      {isCourse && <MediaPlayer />}

      {isCourse &&
        !isMediaPlayerOpen &&
        (productCourse.audioTrailer || productCourse.videoTrailer) && (
          <button onClick={playTrailer} className={styles.playTrailer}>
            <Play /> Play Trailer
          </button>
        )}

      <div className={styles.productHero}>
        {!isCourse && productImages && (
          <ProductImageCarousel
            className="hide-min-large"
            images={productImages}
          />
        )}
        {!isCourse && mainImage && (
          <div className={cx("hide-max-medium", styles.imageColumn)}>
            <Image
              className={styles.productImage}
              imageId={mainImage.asset._id}
              alt={title}
              style={{
                height: "100%",
              }}
            />
          </div>
        )}
        {isCourse &&
          productCourse.courseCover &&
          productCourse.courseCover.asset && (
            <div className={styles.imageColumn}>
              <Image
                className={styles.courseImage}
                imageId={productCourse.courseCover.asset._id}
                alt={title}
                style={{
                  height: "100%",
                }}
              />
            </div>
          )}

        <div className={styles.copyColumn}>
          <div className={styles.copyColumnInner}>
            <div className={styles.copyColumnDetails}>
              <Logotype isCourse={isCourse} className={styles.logotype} />
              {/* <span className={styles.sku}>{product.defaultVariant.sku}</span> */}

              <ProductTitle tag="h1" title={title} />

              <h2 className={cx(styles.price)}>
                <span>
                  <Price
                    defaultPrice={product.defaultPrice}
                    presentmentPrices={activeLiveVariant?.presentmentPrices}
                    showDiscount={true}
                    variantId={activeVariantId}
                  />
                </span>
                {compareAtPrice && (
                  <span
                    style={{
                      textDecoration: "line-through",
                      marginLeft: "1ch",
                    }}
                  >
                    {/* <Price
                      variantId={activeVariantId}
                      defaultPrice={compareAtPrice}
                    /> */}
                    ${compareAtPrice?.amount.replace(".0", "")}
                  </span>
                )}
              </h2>
            </div>

            {product.variants && productOptions && (
              <VariantOptions
                variants={product.variants}
                options={productOptions}
                liveVariants={liveVariants}
                activeVariantId={activeVariantId}
                onVariantSelect={onVariantSelect}
                isFootwear={isFootwear}
              />
            )}
            {isCourse && <CourseHero title={main.title} {...productCourse} />}
            {!isCourse && (
              <div className={styles.copyColumnDetails}>
                {productDetails && (
                  <BlockCopy
                    className={styles.description}
                    copy={productDetails}
                  />
                )}

                {modules &&
                  modules.some(
                    (section: any) =>
                      section.enableNavigationLink &&
                      section.navigationLinkTitle
                  ) && (
                    <ul className={styles.pageNavList}>
                      {modules &&
                        modules.map((section) => {
                          if (
                            section.enableNavigationLink &&
                            section.navigationLinkTitle
                          ) {
                            return (
                              <li key={section._key}>
                                <ScrollLink
                                  key={section._key}
                                  className={styles.pageNavLink}
                                  to={section.navigationLinkTitle}
                                  smooth={true}
                                  duration={500}
                                >
                                  <span className="underline-on-hover">
                                    {section.navigationLinkTitle}
                                  </span>
                                </ScrollLink>
                              </li>
                            );
                          }
                        })}
                    </ul>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
